<template>
  <div class="grid-view-component">
    <el-table
      :resizable="isColumnWidthEditable"
      :border="isColumnWidthEditable"
      ref="el-table"
      :class="tableClass"
      v-loader="isTableLoader"
      :data="extendedListData"
      :show-summary="showSummary"
      :summary-method="getAggregateRow"
      :cell-class-name="columnCellClassName"
      :cell-style="columnCellStyle"
      :row-class-name="rowClassName"
      :row-style="rowStyle"
      row-key="ROW_KEY"
      @expand-change="expandRow"
      @header-dragend="dragHeaderWidth"
    >
      <!--   EditableList edit button   -->
      <el-table-column width="55" v-if="isEditableList && !isEditableMode">
        <template slot-scope="scope">
          <div class="edit-wrapper" @click="handleRowClick(scope.row)">
            <span class="material-icons edit">{{ editState(scope.row) }}</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column v-if="showLockColumn" :fixed="isFixedColumn" width="55">
        <template slot-scope="props">
          <div class="w-100 h-100 d-flex align-items-center justify-content-center">
            <span class="material-icons user-select-none">{{ lockState(props.row) }}</span>
          </div>
        </template>
      </el-table-column>
      <!--   EditableList buttons   -->
      <el-table-column v-if="isEditableMode" width="108">
        <el-button-group slot-scope="scope" v-if="isEditableRow(scope.row)">
          <el-button size="mini" @click="saveRow(scope.row)" type="primary" icon="el-icon-check"></el-button>
          <el-button size="mini" @click="discardEdit" type="primary" icon="el-icon-close"></el-button>
        </el-button-group>
      </el-table-column>

      <!--   Row Selector   -->
      <el-table-column v-if="showSelectionColumn" :fixed="isFixedColumn" width="55">
        <template v-if="isAllRowSelectorVisible" slot="header" slot-scope="scope">
          <div
            class="material-icons md-18 selection-header p-1"
            :class="{ 'empty-checkbox': isEmpty(selectedRows) }"
            @click="handleSelectAll"
          >
            {{ rowSelectorIcon }}
          </div>
        </template>
        <template slot-scope="props">
          <el-checkbox
            :data-id="`selection-box-${props.row.ROWNUMID}`"
            :value="isRowChecked(props.row.ROW_KEY)"
            :disabled="isReadView"
            class="selection-box"
            @change="handleSelectionChange(props.row)"
          />
        </template>
      </el-table-column>
      <!--   Row Expander   -->
      <template>
        <!-- Nem szép, de az element-ui expand scopeja átugrik egy másik columnra ha eltűnik a v-if-el (editableList esetén) -->
        <template v-if="isEditableList">
          <!-- Az expand column-t kondícionálisan v-show / v-ifezzük így lefoglalja előre a helyet és nem ugrál a scopeokban -->
          <el-table-column v-show="showExpandRow" type="expand" :fixed="isFixedColumn" style="color: #270e66">
            <template slot-scope="props">
              <grid-view-expanded-row :row="props.row" :sub-block-url="subBlockUrl" />
            </template>
          </el-table-column>
        </template>
        <template v-else>
          <el-table-column v-if="showExpandRow" type="expand" :fixed="isFixedColumn" style="color: #270e66">
            <template slot-scope="props">
              <grid-view-expanded-row :row="props.row" :sub-block-url="subBlockUrl" />
            </template>
          </el-table-column>
        </template>
      </template>
      <!--   Column   -->
      <el-table-column
        v-for="(column, index) in columnsShown"
        :key="column.prop"
        :prop="column.prop"
        :label="column.label"
        min-width="200"
        :width="getColumnWidth(column.prop)"
        :align="getColumnContentAlignment(column.prop)"
      >
        <!--   Column Header   -->
        <template slot="header" slot-scope="scope" :data-id="`filter-${index}`">
          <!--   Sort   -->
          <div
            class="column-header d-flex align-items-center mb-1"
            :class="{ 'flex-row-reverse': isNumber(column.prop), 'dimmed-row disabled': isEditableMode }"
            @click="setOrder(column)"
          >
            <el-tooltip
              :content="$i18n.t(column.label)"
              placement="top"
              effect="light"
              :open-delay="1000"
              transition="el-zoom-in-top"
            >
              <span class="column-label me-1">{{ $i18n.t(column.label) }}</span>
            </el-tooltip>
            <span class="material-icons md-18 sort" :class="order[column.prop]">{{
              order[column.prop] ? 'sort' : ''
            }}</span>
            <sup>{{ getPriority(column.prop) }}</sup>
          </div>
          <!--   Search   -->
          <search-control
            v-if="showSearchControl && column.searchable"
            :column="column.prop"
            :disabled="isEditableMode"
            :index="index"
          />
        </template>
        <!--   Column Data   -->
        <template slot-scope="scope">
          <a
            v-if="hasPopupRedirect(column.prop)"
            href="javascript:void(0)"
            @click="openDropdownPopup(column, scope.row)"
            class="dropdown-link justify-content-start"
            :class="{ 'dimmed-row disabled': isDisabledRow(scope.row) }"
          >
            <span class="material-icons">keyboard_arrow_right</span>
            <editable-field-wrapper
              v-if="isEditableRow(scope.row)"
              @enter-pressed="saveRow(scope.row)"
              @escape-pressed="discardEdit"
              @set-table-loader="setTableLoader"
              class="w-100"
              :row="scope.row"
              :name="column.prop"
              :list-value="scope.row[column.prop].content"
            />
            <div
              v-else-if="noValue(scope.row[column.prop])"
              class="not-set w-100"
              :class="dynamicContentClass(scope.row[column.prop])"
            >
              {{ notSet }}
            </div>
            <component v-else :is="scope.row[column.prop].formatter" v-bind="scope.row[column.prop]"></component>
          </a>
          <a
            v-else-if="hasRedirect(column.prop)"
            :href="urlByFieldName(column, scope.row)"
            :target="targetByFieldName(column.prop)"
            class="dropdown-link"
            :class="{ 'dimmed-row disabled': isDisabledRow(scope.row) }"
          >
            <span class="material-icons">keyboard_arrow_right</span>
            <editable-field-wrapper
              v-if="isEditableRow(scope.row)"
              @enter-pressed="saveRow(scope.row)"
              @escape-pressed="discardEdit"
              @set-table-loader="setTableLoader"
              class="w-100"
              :row="scope.row"
              :name="column.prop"
              :list-value="scope.row[column.prop].content"
            />
            <div
              v-else-if="noValue(scope.row[column.prop])"
              class="not-set w-100"
              :class="dynamicContentClass(scope.row[column.prop])"
            >
              {{ notSet }}
            </div>
            <component v-else :is="scope.row[column.prop].formatter" v-bind="scope.row[column.prop]"></component>
          </a>
          <a
            v-else-if="showExpandRow"
            class="dropdown-link el-link el-link--default"
            href="javascript:void(0)"
            @click.prevent="handleDataClick(scope.row)"
            @dblclick="handleDoubleClick(scope.row)"
            :class="{ 'dimmed-row disabled': isDisabledRow(scope.row) }"
          >
            <template>
              <editable-field-wrapper
                v-if="isEditableRow(scope.row)"
                @enter-pressed="saveRow(scope.row)"
                @escape-pressed="discardEdit"
                @set-table-loader="setTableLoader"
                class="w-100"
                :row="scope.row"
                :name="column.prop"
                :list-value="scope.row[column.prop].content"
              />
              <div
                v-else-if="noValue(scope.row[column.prop])"
                class="not-set w-100"
                :class="dynamicContentClass(scope.row[column.prop])"
              >
                {{ notSet }}
              </div>
              <component v-else :is="scope.row[column.prop].formatter" v-bind="scope.row[column.prop]"></component>
            </template>
          </a>
          <div v-else class="d-flex align-items-center" :class="{ 'dimmed-row disabled': isDisabledRow(scope.row) }">
            <template>
              <editable-field-wrapper
                v-if="isEditableRow(scope.row)"
                @enter-pressed="saveRow(scope.row)"
                @escape-pressed="discardEdit"
                @set-table-loader="setTableLoader"
                class="w-100"
                :row="scope.row"
                :name="column.prop"
                :list-value="scope.row[column.prop].content"
              />
              <div
                v-else-if="noValue(scope.row[column.prop])"
                class="not-set w-100"
                :class="dynamicContentClass(scope.row[column.prop])"
              >
                {{ notSet }}
              </div>
              <component v-else :is="scope.row[column.prop].formatter" v-bind="scope.row[column.prop]"></component>
            </template>
          </div>
        </template>
        <!--        @todo kell-e megnyitas gomb...-->
        <!--        <template slot-scope="scope">-->
        <!--          <el-button-->
        <!--            size="mini"-->
        <!--            @click="() => {(scope.$index, scope.row)}">Edit</el-button>-->
        <!--        </template>-->
      </el-table-column>
      <!--   Empty List Info   -->
      <template slot="empty">
        <div class="alert alert-warning alert-dismissable mb-0">{{ $i18n.t(getListInfo) }}!</div>
      </template>
      <!--   Infinite Loader   -->
      <template v-if="showInfiniteLoader" slot="append">
        <loader ref="infinite" :is-in-line="true"></loader>
      </template>
    </el-table>
    <!--  EditableList's add new row button  -->
    <div v-if="showAddRowButton" class="p-2 add-button-container">
      <el-button icon="el-icon-plus" @click="addRow" :disabled="isEditableMode">{{ $i18n.t('add_new_row') }}</el-button>
    </div>
  </div>
</template>

<script>
import GridViewExpandedRow from './GridViewExpandedRow'
import Loader from '../../../Loader'
import SearchControl from '../../search/SearchControl'
import file from '../../../../utils/file'
import listRest from '../../../../utils/mixin-list-rest'
import mixinEditableList from '../../../../utils/mixin-editable-list'
import { mapActions } from 'vuex'
import { createStorage, floatToPrecisionString, hasValue, resolveUrlParams } from '../../../../utils'
import { parseMultiColumnName } from '../../../../utils/list'
import {
  cloneDeep,
  flatten,
  get,
  intersection,
  isEmpty,
  isEqual,
  isNull,
  map,
  some,
  xor,
  forEach,
  uniq,
  find,
  isString,
  pick,
} from 'lodash'
import EventBus from '../../../../utils/event-bus'
import {
  DEFAULT_PAGER,
  QUERY_BUILDER_COLUMN_FORMATS,
  AGGREGATABLE_COLUMN_TYPES,
  AGGREGATION_OFF,
  PROP_ROW_KEY,
} from '../../lib/list-constants'
import pagination from '../../../../utils/mixin-pagination'

import CamerasField from '../../field-types/CamerasField'
import CheckBoxField from '../../field-types/CheckBoxField'
import CheckBoxListField from '../../field-types/CheckBoxListField'
import DateField from '../../field-types/DateField'
import ExpressionField from '../../field-types/ExpressionField'
import GpsField from '../../field-types/GpsField'
import NumberField from '../../field-types/NumberField'
import PasswordField from '../../field-types/PasswordField'
import RadioButtonListField from '../../field-types/RadioButtonListField'
import RatingField from '../../field-types/RatingField'
import SignatureField from '../../field-types/SignatureField'
import SubFormField from '../../field-types/SubFormField'
import TextField from '../../field-types/TextField'
import BaseRange from '../../../form/controls/BaseRange'
import EditableFieldWrapper from '../../editable-field-types/EditableFieldWrapper'
import { FIELD_TYPES } from '@/consts'

const DEFAULT_DECIMAL_PLACES = 2
const ID_SUFFIX = '-ID'

export default {
  name: 'GridView',
  mixins: [file, listRest, mixinEditableList, pagination],
  components: {
    GridViewExpandedRow,
    Loader,
    SearchControl,
    CamerasField,
    CheckBoxField,
    CheckBoxListField,
    DateField,
    ExpressionField,
    GpsField,
    NumberField,
    PasswordField,
    RadioButtonListField,
    RatingField,
    SignatureField,
    SubFormField,
    BaseRange,
    TextField,
    EditableFieldWrapper,
  },
  props: ['isReadView'],
  data() {
    return {
      currentView: {
        name: 'gridView',
      },
      search: '',
      selectedRowsData: [],
    }
  },
  computed: {
    notSet() {
      return this.$i18n.t('not_set')
    },
    tableClass() {
      let classNames = 'grid-view-container'
      if (this.isColumnWidthEditable && this.showLockColumn) {
        classNames += ' column-editable'
      }
      return classNames
    },
    isColumnWidthEditable() {
      return get(this.extendedOptions, 'isColumnWidthEditable', false)
    },
    actionsInlineVisible() {
      return this.showActionButtons && this.extendedOptions.actionButtonsStyle === 'Inline'
    },
    aggregations() {
      return this.$get('aggregations')
    },
    aggregateRow() {
      return this.$get('aggregateRow') || []
    },
    columns() {
      return this.$get('columns')
    },
    columnsShown() {
      const columns = []
      for (const name of this.showInList) {
        const column = this.tableData.find((column) => parseMultiColumnName(column.prop) === name)
        if (column) columns.push(column)
      }
      return columns
    },
    controllerName() {
      return this.$get('controllerName')
    },
    defaultEmpty() {
      return this.$get('defaultEmpty')
    },
    docMTNList() {
      return this.$get('docMTNList')
    },
    isProcessList() {
      return this.$get('isProcessList')
    },
    expandedRows: {
      get() {
        return this.$get('expandedRows')
      },
      set(rows) {
        this.localStore.setItemValue('expandedRows', rows)
        this.$dispatch('setExpandedRows', { rows })
      },
    },
    extendedOptions() {
      return this.$get('extendedOptions')
    },
    fetchingListData() {
      return this.$get('fetchingListData')
    },
    fieldName() {
      return this.$get('fieldName')
    },
    getControllerName() {
      if (this.isInDashboard && !this.controllerName) {
        return
      }
      if (!this.controllerName && window.CORE && window.CORE.requestController && !this.isRelationshipList) {
        return window.CORE.requestController
      }
      return this.controllerName
    },
    getListInfo() {
      if (!this.listData.length && this.isSearchBarActive) {
        return 'list_search_empty'
      }
      if (!this.listData.length && this.isDefaultEmpty) {
        return 'please_search'
      }
      return this.fetchingListData ? 'list_loading' : 'list_empty'
    },
    groupActionsRunning() {
      return this.$get('groupActionsRunning')
    },
    id() {
      return this.$get('id')
    },
    infiniteScroll() {
      return this.$get('infiniteScroll')
    },
    isAllRowSelectorVisible() {
      return this.isMultiselect
    },
    isCollapsed() {
      return this.$get('isCollapsed')
    },
    isDefaultEmpty() {
      return this.defaultEmpty && !this.selectedRow
    },
    // @todo egyszer majd faszan lekezeljuk az expanded row tartalmat sticky oszlopok mellett
    isExpanded() {
      return this.visibilityState({ key: 'isExpanded' })
    },
    isFixedColumn() {
      return false
      // return this.expandedRows.length
      //   ? false
      //   : 'left'
    },
    isInDashboard() {
      return this.$get('isInDashboard')
    },
    isMultiselect() {
      return this.$get('isMultiselect')
    },
    isRelationshipList() {
      return this.$get('isRelationshipList')
    },
    isSearchBarActive() {
      const searchParams = cloneDeep(this.searchParams)
      delete searchParams.start
      return !(
        isEmpty(searchParams) &&
        this.selectedRowsFilter === 'normal' &&
        isEmpty(get(this.universalFilter, 'value', ''))
      )
    },
    isSqlChosen() {
      return this.$get('isSqlChosen')
    },
    isTableLoader() {
      return (!this.infiniteScroll && (this.fetchingListData || this.groupActionsRunning)) || this.loading
    },
    listData() {
      return this.$get('listData')
    },
    extendedListData() {
      const data = structuredClone(this.$get('listData'))
      const propsToPick = [
        'data',
        'format',
        'type',
        'thousandSeparated',
        'originalType',
        'numberPrefix',
        'numberSuffix',
        'numberOfDecimals',
      ]
      for (const column of this.columns) {
        const cell = pick(column, propsToPick)
        const formatter = this.getFormatter(cell)

        for (const row of data) {
          const index = data.indexOf(row)
          const content = get(row, cell.data)
          data[index][cell.data] = { ...cell, content, formatter }
        }
      }
      return data
    },
    locale() {
      return get(this.$CORE, 'language.code', '').substring(0, 2)
    },
    mainTable() {
      return (this.primaryKey && this.primaryKey.substring(3)) || ''
    },
    mongoId() {
      return this.$get('mongoId')
    },
    mongoList() {
      return this.$get('mongoList')
    },
    order: {
      get() {
        return this.$get('orderState')
      },
      set(column) {
        let direction = this.$get('orderState')[column]
        switch (direction) {
          case 'asc':
            direction = 'desc'
            break
          case 'desc':
            direction = null
            break
          default:
            direction = 'asc'
        }
        this.$dispatch('setOrder', { column, value: direction })
        this.$dispatch('setPager', {
          prop: 'offset',
          value: DEFAULT_PAGER.offset,
        })
        if (!this.orderParams.length) {
          this.localStore.removeItemValue('orderParams')
        } else {
          this.localStore.setItemValue('orderParams', this.orderParams)
        }
        EventBus.$emit(`apply-filter-${this.id}`, { id: this.id, type: 'button' })
      },
    },
    orderParams() {
      if (isEmpty(this.order)) return []
      return map(this.order, (value, key) => {
        return { column: key, dir: value }
      })
    },
    panelId() {
      const _panelId = this.$get('panelId')
      return isString(_panelId) ? _panelId : null
    },
    primaryKey() {
      return this.$get('primaryKey')
    },
    queryId() {
      return this.$get('queryId')
    },
    rawSqlId() {
      return this.$get('rawSqlId')
    },
    recordsTotal() {
      return this.$get('recordsTotal')
    },
    rowDetails: {
      get() {
        return this.$get('rowDetails')
      },
      set({ index, value }) {
        this.$dispatch('setRowDetails', { index, value })
      },
    },
    rowSelectorIcon() {
      const selectedElementsOnPage = intersection(this.selectedRows, map(this.extendedListData, PROP_ROW_KEY))
      if (isEmpty(this.selectedRows) || isEmpty(selectedElementsOnPage)) {
        return 'check_box_outline_blank'
      } else {
        if (selectedElementsOnPage.length === this.listData.length) {
          return 'indeterminate_check_box'
        } else {
          return 'check_box'
        }
      }
    },
    searchOnButtonPress() {
      return get(this.extendedOptions, 'searchKeypress', false)
    },
    searchParams() {
      if (isEmpty(this.columns)) {
        return []
      }
      return this.columns
        .filter((col) => this.isColumnVisible(col.data) && (col.value || some(col.interval)))
        .reduce((accu, col) => {
          const prop = col.interval ? 'interval' : 'value'
          accu = [
            ...accu,
            {
              data: col.data,
              value: col[prop],
            },
          ]
          return accu
        }, [])
    },
    selectedRow() {
      return this.$get('selectedRow')
    },
    selectedRows: {
      get() {
        return this.$get('selectedRows')
      },
      set(value) {
        this.$dispatch('setSelectedRows', { value })
      },
    },
    selectedRowsFilter() {
      return this.$get('selectedRowsFilter')
    },
    showActionButtons() {
      return this.visibilityState({ key: 'showActionButtons' })
    },
    showDetails() {
      return this.$get('showDetails')
    },
    showDetailView() {
      return this.visibilityState({ key: 'showDetailView' })
    },
    showExpandRow() {
      return (
        !this.isSqlChosen &&
        !this.isCollapsed &&
        (this.showActionButtons || this.showDetailView) &&
        !this.isEditableMode
      )
    },
    showFilters() {
      return this.visibilityState({ key: 'showFilters' })
    },
    showInfiniteLoader() {
      return this.infiniteScroll && this.listData.length && this.listData.length !== this.recordsTotal
    },
    showInList() {
      return this.$get('showInList')
    },
    showLockColumn() {
      return get(this.extendedOptions, 'showLockColumn', false) && !this.isEditableMode
    },
    showSearchControl() {
      return this.showFilters && !this.isCollapsed && (!this.isSqlChosen || (this.isSqlChosen && this.isExpanded))
    },
    showSelectionColumn() {
      return this.visibilityState({ key: 'showSelectionColumn' })
    },
    showSummary() {
      const aggregations = this.aggregations
        ? this.aggregations.filter((aggregation) => aggregation.aggr !== 'Off')
        : []
      return !isEmpty(aggregations) && !isEmpty(intersection(map(aggregations, 'column'), this.showInList))
    },
    subBlockUrl() {
      return this.$get('subBlockUrl')
    },
    tableData() {
      return this.columns.map((column) => {
        return {
          label: column.title,
          prop: column.data,
          searchable: column.searchable,
          orderable: column.orderable,
          relTable: column.relTable,
        }
      })
    },
    universalFilter() {
      return this.$get('universalFilter')
    },
    visibilityState() {
      return this.$get('visibilityState')
    },
    hasDbClickRedirect() {
      return get(this.extendedOptions, 'dbClickRedirect.active', false)
    },
  },
  methods: {
    ...mapActions('popup', ['setOpened', 'setType', 'setUrl']),
    appendSearchButton() {
      this.$nextTick(() => {
        const header = document.querySelector('.el-table__header-wrapper')
        const searchLabel = this.$i18n.t('apply_filters')
        header.insertAdjacentHTML(
          'afterend',
          `<div class='el-button el-button--success is-plain rounded-0 border-0 w-100' id='search'>${searchLabel}</div>`
        )
        const button = document.querySelector('#search')
        button.addEventListener('click', this.onApplyFilter)
      })
    },
    buttonsParameters(id, rowData) {
      return {
        docMTNList: this.docMTNList,
        formId: this.mongoId,
        queryId: this.queryId,
        rawSqlId: this.rawSqlId,
        mongoList: JSON.stringify(this.mongoList),
        processList: this.isProcessList,
        primaryKey: id,
        table: this.mainTable,
        controllerName: this.getControllerName,
        showDetails: this.showDetails,
        panelId: this.panelId,
        options: JSON.stringify(this.extendedOptions),
        instance: JSON.stringify(rowData),
      }
    },
    canAggregate(colData) {
      const columnProps = this.columns.find((col) => col.data === colData)
      return columnProps && AGGREGATABLE_COLUMN_TYPES.includes(columnProps.type)
    },
    checkDecimalPlaces(value) {
      if (value && value.includes('.')) {
        return value.split('.')[1].length
      } else {
        return 0
      }
    },
    checkVisible(wrapper) {
      const wrapperRect = wrapper?.getBoundingClientRect()
      const bottom = wrapperRect.top + wrapperRect.height
      const loader = get(this.$refs, ['infinite', '$el'], null)
      if (!loader) return
      const loaderRect = loader.getBoundingClientRect()
      if (loaderRect.top < bottom) {
        if (this.fetchingListData) return
        this.loadNextPage()
      }
    },
    colType(col) {
      return get(col, 'originalType') || get(col, 'type') || ''
    },
    expandAllRow() {
      this.$nextTick(() => {
        this.$refs['el-table'].tableData.forEach((row) => {
          this.$refs['el-table'].toggleRowExpansion(row, true)
        })
      })
    },
    async expandRow(rowData, expandedRows) {
      this.expandedRows = expandedRows.filter((row) => row.ROW_KEY).map((row) => row.ROW_KEY)
      const id = rowData.ROW_KEY
      if (!this.expandedRows.includes(id)) return
      const index = this.getListId(id)
      this.$nextTick(() => this.scrollDetailIntoView(index))
      const detailOption = { ...this.rowDetails[id] }
      const instance = this.extendedListData.find((data) => data.ROW_KEY === id)
      if (detailOption.status === 'success') return
      try {
        const buttonList = await this.getButtonList(this.buttonsParameters(id, instance))
        let functions = get(buttonList, 'obj.data.functions', [])
        functions = functions.map((button) => {
          if (button.type === 'nodeProcess') {
            const hasNoApiBasePathInUrl = button.url.indexOf(window.CORE.apiBasePath) === -1
            if (hasNoApiBasePathInUrl) {
              button.url = `${button.url}`
            }
          }
          button.url = resolveUrlParams(button, this.$route)
          return button
        })
        // button overrides
        if (!this.isProcessList) {
          functions = this.resolveProcessButtonOverrides(functions)
        }
        detailOption.status = 'success'

        const idName = this.primaryKey
        const detailViewOptions = Object.assign(
          {},
          {
            hiddenSearchData: { where: [{ key: idName, value: id }] },
            mongoList: this.mongoList,
            docMTNList: this.docMTNList,
            processList: this.isProcessList,
            mongoId: this.mongoId,
            queryId: this.queryId,
            extraOptions: this.extendedOptions,
            panelId: this.panelId,
          }
        )
        Object.assign(detailOption, {
          options: { view: { properties: { ...detailViewOptions } } },
        })
        Object.assign(detailOption.options, { functions })
        this.rowDetails = Object.assign({}, this.rowDetails, {
          index: id,
          value: { ...detailOption, status: 'success' },
        })
      } catch (error) {
        detailOption.status = 'fail'
        detailOption.errorMessage =
          error.message && error.message.includes('Ldata is undefined')
            ? this.$i18n.t('list_data_is_undefined')
            : error.message
      }
    },
    formatAggregationValue(value, type) {
      if (type === 'number') {
        if (!value) return '0'
        value = String(value)

        const decimalPlaces = this.checkDecimalPlaces(value)
        const precision = decimalPlaces <= DEFAULT_DECIMAL_PLACES ? decimalPlaces : DEFAULT_DECIMAL_PLACES

        value = floatToPrecisionString(value, precision)

        if (this.locale === 'en') {
          value = value.replace(',', '.').replace(' ', ',')
        }
        return value
      }
      return value
    },
    get,
    getAggregateRow(param) {
      const { columns } = param
      const aggregations = []
      columns.forEach((gridColumn, index) => {
        const column = this.columns.find((column) => column.data === gridColumn.property)
        if (this.canAggregate(gridColumn.property) && this.hasAggregation(gridColumn.property)) {
          const type = this.getAggregationType(gridColumn.property)
          if (type !== AGGREGATION_OFF) {
            const value = this.getAggregationValue(gridColumn.property, column.type)
            aggregations[index] = `${this.$i18n.t(`${type}_:`)} ${!isNull(value) ? value : this.$i18n.t('not_set')}`
          }
        }
      })
      return aggregations
    },
    getAggregationType(colData) {
      const aggregation = this.aggregations.find((aggregation) => aggregation.column === colData)
      return aggregation.aggr
    },
    getAggregationValue(colData, colType) {
      if (isEmpty(this.aggregateRow)) return ''
      const aggregationValue = flatten(this.aggregateRow).find((columnValue) => columnValue.hasOwnProperty(colData))
      return aggregationValue ? this.formatAggregationValue(aggregationValue[colData], colType) : ''
    },
    getColumnWidth(prop) {
      if (prop && this.isColumnWidthEditable) {
        const localStorageKeyName = `column_width_${get(window, 'CORE.params.idUrl', '')}_${this.panelId}`
        let storedWidths
        try {
          storedWidths = JSON.parse(localStorage.getItem(localStorageKeyName)) || []
        } catch (e) {
          storedWidths = []
        }
        const current = storedWidths.find(({ property }) => property === prop)
        return get(current, 'width', undefined)
      }
      return undefined
    },
    getColumnContentAlignment(colData) {
      return this.isNumber(colData) ? 'right' : this.isDate(colData) ? 'center' : 'left'
    },
    getPriority(column) {
      return this.orderParams.findIndex((orderParam) => column === orderParam.column) + 1 || ''
    },
    handleDataClick(row) {
      if (this.isEditableList) return
      if (!this.hasDbClickRedirect) return this.$refs['el-table'].toggleRowExpansion(row)
    },
    async handleDoubleClick(row) {
      if (this.isEditableList) {
        this.handleRowClick(row)
      } else if (this.hasDbClickRedirect) {
        const rowId = row[PROP_ROW_KEY]
        const redirect = get(this.extendedOptions, 'dbClickRedirect', {})
        let url = this.resolveOnclickRedirect(redirect.url, rowId)

        if (!url) {
          const { body } = await this.getButtonList(this.buttonsParameters(rowId, row))
          url = get(body, 'data.functions[0].url')
        }

        if (redirect.popup) {
          this.openPopup(url, redirect.formType)
        } else {
          this.$router.push(url)
        }
      }
    },
    handleSelectAll() {
      const listItems = map(this.extendedListData, PROP_ROW_KEY)
      const selectedRows = cloneDeep(this.selectedRows)
      if (selectedRows.length && selectedRows.includes(listItems[0])) {
        listItems.forEach((value) => {
          const idx = selectedRows.findIndex((rowId) => rowId === value)
          if (idx > -1) {
            selectedRows.splice(idx, 1)
          }
        })
        this.selectedRows = [...selectedRows]
      } else {
        this.selectedRows = uniq([...selectedRows, ...listItems])
      }

      this.extendedListData.forEach((row) => this.getSelectedRowsData(row))

      EventBus.$emit('sql-select-change-by-gridview', {
        name: this.fieldName,
        value: this.selectedRows,
        data: this.selectedRowsData,
      })
    },
    getSelectedRowsData(row) {
      if (this.isMultiselect) {
        return xor(this.selectedRowsData, [row])
      } else {
        return isEqual(this.selectedRowsData[0], row) ? [] : [row]
      }
    },
    handleSelectionForSqlChosen(row) {
      this.selectedRowsData = this.getSelectedRowsData(row)

      EventBus.$emit('sql-select-change-by-gridview', {
        name: this.fieldName,
        value: this.selectedRows,
        data: this.selectedRowsData,
      })
    },
    handleSelectionChange(row) {
      if (this.isSqlChosen && !this.isExpanded) return
      const rowId = row.ROW_KEY
      this.selectedRows = this.isMultiselect
        ? xor(this.selectedRows, [rowId])
        : isEqual(this.selectedRows[0], rowId)
          ? []
          : [rowId]

      if (this.isSqlChosen) this.handleSelectionForSqlChosen(row)
    },
    hasAggregation(colData) {
      return this.aggregations.find((aggregation) => aggregation.column === colData)
    },
    isColumnVisible(colData) {
      return this.showInList && this.showInList.includes(parseMultiColumnName(colData))
    },
    isDate(colData) {
      const col = this.columns.find((column) => column.data === colData)
      return (
        [QUERY_BUILDER_COLUMN_FORMATS.date, QUERY_BUILDER_COLUMN_FORMATS.datetime].includes(col.format) ||
        ['datetime-local', 'date', 'time'].includes(this.colType(col))
      )
    },
    isEmpty,
    isNumber(colData) {
      const col = this.columns.find((column) => column.data === colData)
      return col.format === QUERY_BUILDER_COLUMN_FORMATS.number || this.colType(col) === 'number'
    },
    isRowChecked(rowKey) {
      return this.selectedRows.includes(rowKey)
    },
    isRowSelected(rowKey) {
      return rowKey && String(rowKey) === String(this.selectedRow)
    },
    getListId(rowKey) {
      return this.extendedListData.findIndex((row) => row[PROP_ROW_KEY] === rowKey)
    },
    loadNextPage() {
      this.$dispatch('setPager', {
        prop: 'offset',
        value: this.offset + this.limit,
      })
      if (this.localStore) this.localStore.setItemValue('pager', this.pager)
      EventBus.$emit(`apply-filter-${this.id}`, { id: this.id, type: 'scroll' })
    },
    editState(row) {
      return get(row, 'RECORD_LOCK.isLocked', false) ? 'edit_off' : 'edit'
    },
    lockState(row) {
      return get(row, 'RECORD_LOCK.isLocked', false) ? 'lock' : 'lock_open'
    },
    openPopup(url, formType = 'DynForm') {
      this.setUrl(url)
      this.setType(formType)
      this.setOpened()
    },
    resolveOnclickRedirect(url, rowId) {
      if (!url) return null
      url = url.replace('${rowId}', rowId).replace(':rowId', rowId)
      return resolveUrlParams({ url })
    },
    resolveProcessButtonOverrides(processButtons) {
      if (this.extendedOptions.buttons) {
        processButtons.forEach((button) => {
          const processAction = this.extendedOptions.buttons.find(
            (extButton) =>
              extButton.selector === button.processId ||
              extButton.selector === button.selector ||
              button.url.match(extButton.selector)
          )
          if (processAction && processAction.name) {
            button.name = processAction.name
          }
          //@todo: refactor
          if (processAction && processAction.emitEvent) {
            button.emitEvent = processAction.emitEvent
          }

          if (processAction && processAction.popup) {
            button.popup = processAction.popup
          }

          if (processAction && processAction.params) {
            button.params = processAction.params
          }
        })
      }
      return cloneDeep(processButtons)
    },
    rowClassName({ row }) {
      const classes = get(this.$get('rowClasses'), row[PROP_ROW_KEY], '')
      return `row-${row[PROP_ROW_KEY]} ${classes}`
    },
    rowStyle({ row }) {
      return get(this.$get('rowStyles'), row[PROP_ROW_KEY], '')
    },
    columnCellClassName({ column, row }) {
      const columnClasses = get(this.$get('columnClasses'), column.property, '')
      const cellClasses =
        find(this.$get('cellClasses'), (cell) => cell.field === column.property && cell.rowKey === row[PROP_ROW_KEY])
          ?.class ?? ''

      return `${columnClasses} ${cellClasses}`
    },
    columnCellStyle({ column, row }) {
      const columnStyles = get(this.$get('columnStyles'), column.property, '')
      const cellStyles =
        find(this.$get('cellStyles'), (cell) => cell.field === column.property && cell.rowKey === row[PROP_ROW_KEY])
          ?.style ?? {}

      return Object.assign(cloneDeep(columnStyles), cloneDeep(cellStyles))
    },
    scrollDetailIntoView(index) {
      if (this.actionsInlineVisible) return
      const elTable = this.$refs['el-table']
      if (!elTable) return
      const table = elTable.$el
      const rows = table.querySelectorAll('.el-table__row')
      const row = get(rows, [index])
      const detail = get(row, 'nextSibling')
      const wrapper = document.querySelector('.el-table__body-wrapper')
      if (rows && detail && wrapper) {
        const wrapperRect = wrapper.getBoundingClientRect()
        const tableRect = table?.getBoundingClientRect()
        const rowRect = row?.getBoundingClientRect()
        const topDistance = rowRect.top - wrapperRect.top
        let scrollHeight
        if (this.isEditableMode) {
          scrollHeight = topDistance
        } else {
          if (detail instanceof HTMLElement) {
            const detailRect = detail.getBoundingClientRect()
            const bottomDistance = detailRect.bottom - tableRect.bottom
            scrollHeight = bottomDistance < topDistance ? bottomDistance : topDistance
          }
        }
        if (scrollHeight > 0) {
          wrapper.style.scrollBehavior = 'smooth'
          wrapper.scrollBy(0, scrollHeight)
        }
      }
    },
    setOrder(column) {
      if (!column.orderable) return
      this.order = column.prop
    },
    onApplyFilter() {
      EventBus.$emit(`apply-filter-${this.id}`, { id: this.id, type: 'button' })
    },
    hasPopupRedirect(fieldName) {
      let res = false
      forEach(this.extendedOptions.columnRedirect, (i) => {
        if (i.columnName === fieldName && i.popup) {
          res = true
        }
      })
      return res
    },
    hasRedirect(fieldName) {
      let res = false
      forEach(this.extendedOptions.columnRedirect, (i) => {
        res = i.columnName === fieldName
      })
      return res
    },
    urlByFieldName(column, row) {
      const relTable = column.relTable
      const fieldName = column.prop
      const id = get(row, `${fieldName}${ID_SUFFIX}`)
      const url = `/${relTable.toLowerCase().replaceAll('_', '-')}/update?ID_${relTable}=${id}`
      return url
    },
    targetByFieldName(fieldName) {
      let target = ''
      forEach(this.extendedOptions.columnRedirect, (i) => {
        if (i.columnName === fieldName) {
          target = i.target
        }
      })
      return target
    },
    openDropdownPopup(column, row) {
      let url = this.urlByFieldName(column, row)
      this.openPopup(url)
    },
    dragHeaderWidth(newWidth, oldWidth, column) {
      const localStorageKeyName = `column_width_${get(window, 'CORE.params.idUrl', '')}_${this.panelId}`
      const editedColumn = {
        width: newWidth,
        property: column.property,
      }

      let localColumnProps

      try {
        localColumnProps = JSON.parse(localStorage.getItem(localStorageKeyName)) || []
      } catch (e) {
        localColumnProps = []
      }

      const existingIndex = localColumnProps.findIndex(
        (localColumnProp) => localColumnProp.property === editedColumn.property
      )
      if (existingIndex > -1) {
        localColumnProps[existingIndex].width = newWidth
      } else {
        localColumnProps.push(editedColumn)
      }

      localStorage.setItem(localStorageKeyName, JSON.stringify(localColumnProps))
    },
    setTableLoader(value) {
      this.loading = value
    },
    getFormatter(cell) {
      if (cell.format === QUERY_BUILDER_COLUMN_FORMATS.document || cell.type === FIELD_TYPES.CAMERAS) {
        return CamerasField
      } else if ([FIELD_TYPES.CHECKBOX, FIELD_TYPES.BOOLEAN].includes(cell.type)) {
        return CheckBoxField
      } else if ([FIELD_TYPES.CHECKBOX_LIST, FIELD_TYPES.MULTISELECT, FIELD_TYPES.SQL_CHOSEN].includes(cell.type)) {
        return CheckBoxListField
      } else if (cell.type.match(/date|time/g)) {
        // @regex date vagy time szerepel a type-ban
        return DateField
      } else if (cell.type === FIELD_TYPES.EXPRESSION_EDITOR) {
        return ExpressionField
      } else if (cell.type === FIELD_TYPES.GPS) {
        return GpsField
      } else if (cell.format === QUERY_BUILDER_COLUMN_FORMATS.number || cell.type === FIELD_TYPES.NUMBER) {
        return NumberField
      } else if (cell.type === FIELD_TYPES.PASSWORD) {
        return PasswordField
      } else if (cell.type === FIELD_TYPES.RADIO_BUTTON_LIST) {
        return RadioButtonListField
      } else if (cell.type === FIELD_TYPES.RATING) {
        return RatingField
      } else if ([FIELD_TYPES.W_PAINT, FIELD_TYPES.SIGNATURE].includes(cell.type)) {
        return SignatureField
      } else if (cell.type === FIELD_TYPES.SUB_FORM) {
        return SubFormField
      } else if (cell.type === FIELD_TYPES.SLIDER) {
        return BaseRange
      } else {
        return TextField
      }
    },
    noValue(cell) {
      return (
        ![FIELD_TYPES.SLIDER, FIELD_TYPES.RATING, FIELD_TYPES.NUMBER].includes(cell.type) &&
        typeof cell.content !== FIELD_TYPES.BOOLEAN &&
        !hasValue(cell.content)
      )
    },
    dynamicContentClass(cell) {
      return this.inDetailView
        ? 'text-start'
        : this.$get('viewMode') === 'gridView' &&
            ((this.noValue(cell) && cell.type === FIELD_TYPES.EMAIL) ||
              [FIELD_TYPES.CHECKBOX, FIELD_TYPES.BOOLEAN].includes(cell.type) ||
              cell.type.match(/date|time/g) ||
              cell.type === FIELD_TYPES.PASSWORD ||
              cell.type === FIELD_TYPES.RATING ||
              [FIELD_TYPES.W_PAINT, FIELD_TYPES.SIGNATURE].includes(cell.type))
          ? 'text-center'
          : cell.format === QUERY_BUILDER_COLUMN_FORMATS.number || cell.type === FIELD_TYPES.NUMBER
            ? 'text-end'
            : 'text-start'
    },
  },
  created() {
    this.localStore = createStorage(this.$get('storageKey'))
  },
  async mounted() {
    if (this.isEditableList) await this.setFormProperties()
    if (this.infiniteScroll) {
      this.$nextTick(() => {
        let selector
        const panelUid = this.$get('panelUid')
        if (panelUid) {
          selector = `[paneluid="${panelUid}"] div.el-table__body-wrapper`
        } else {
          selector = 'div.el-table__body-wrapper'
        }
        const wrapper = document.querySelector(selector)
        if (wrapper) wrapper.onscroll = () => this.checkVisible(wrapper)
      })
    }
    if (this.showSearchControl && this.searchOnButtonPress) {
      this.appendSearchButton()
    }

    const expandedRowsFromStore = get(this.localStore.getStoreContent(), 'expandedRows', [])
    if (!isEmpty(expandedRowsFromStore)) {
      this.expandedRows = expandedRowsFromStore
    }
  },
  watch: {
    listData(value) {
      if (this.actionsInlineVisible && value.length) {
        this.expandAllRow()
      } else {
        this.$nextTick(() => {
          if (!isEmpty(value)) {
            this.expandedRows.forEach((rowKey) => {
              const row = this.extendedListData.find((iteratedRow) => iteratedRow.ROW_KEY === rowKey)
              if (row) this.$refs['el-table'].toggleRowExpansion(row, true)
            })
          }
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../../assets/sass/components/vuelistas';

.grid-view-container {
  width: 100%;
  border: 1px solid $border-color;
  border-radius: $border-radius;

  .material-icons.empty-checkbox {
    color: $light3;
  }
  .column-header {
    cursor: pointer;
    .column-label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .selection-header {
    cursor: pointer;
    padding: 4px;
  }
  .selection-box {
    padding-left: 7px;
  }
  .sort.asc {
    transform: rotateX(180deg);
  }
}
.button-search-width {
  width: 60%;
  margin: 0 auto;
}
tr.item-row:not(.active):hover td {
  background-color: $light2 !important;
}
.text-left {
  .tooltip-container {
    position: fixed;
    background-color: white;
    color: #42527e;
    padding: 0;
    max-width: 80%;
    height: fit-content;
    max-height: 135px;
    overflow-y: auto;
    border: 10px solid white;
    border-radius: 14px;
    box-shadow: rgba(0, 0, 0, 0.25) 2px 2px 10px;
    z-index: 1;
  }
  .tooltip-container,
  .tooltip-container:after {
    visibility: hidden;
    opacity: 0;
    transition: all 0.3s;
  }
  .tooltip-container .tooltip-content {
    padding: 0 5px;
  }
}
.text-left:hover {
  .tooltip-container,
  .tooltip-container:after {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.3s;
  }
}
.list-aggregate-row td {
  background-color: $light2 !important;
  font-weight: bold;
}
.dropdown-link {
  display: flex;
  align-items: center;
  color: $blue;
  cursor: pointer;

  .material-icons {
    margin-right: 6px;
    font-size: 13px;
    border: 1px solid $blue;
    border-radius: 50%;
    padding: 0px;
  }
  &:hover p {
    color: $color-primary-hover;
  }
  &:hover span {
    background-color: $color-primary-hover;
    color: $white;
    border: 1px solid $color-primary-hover;
    transition: 0.4s;
  }
}
.add-button-container {
  border: 1px solid $border-color;
  border-radius: $default-border-radius;
  border-top: none;
}

.dimmed-row {
  opacity: 0.5 !important;
}
.material-icons.edit {
  font-size: 1.5em;
  align-self: center;
}
.edit-wrapper {
  color: whitesmoke;
  padding: 0.5em;
  border-radius: 0.5em;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $default-panel-header-bg;
  transition: 0.3s all;
}
.edit-wrapper:hover {
  transform: scale(0.8);
  color: $default-panel-header-bg;
  background-color: $border-color;
}
</style>
